import React from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { Mail, Linkedin, X, Github, Instagram, Activity, CheckCircle, AlertTriangle, Wrench } from "lucide-react"; // Import icons for social media
import healthService from "@/services/healthService";

const STATUS_ICON = {
  healthy: <CheckCircle className="w-3 h-3 text-sauna-green" />,
  degraded: <AlertTriangle className="w-3 h-3 text-sauna-orange" />,
  unhealthy: <Activity className="w-3 h-3 text-sauna-orange" />,
  maintenance: <Wrench className="w-3 h-3 text-sauna-blue" />,
  unknown: <Activity className="w-3 h-3 text-sauna-grey" />,
};
const STATUS_LABEL = {
  healthy: "All systems operational",
  degraded: "Degraded",
  unhealthy: "Major outage",
  maintenance: "Maintenance",
  unknown: "Unknown",
};

const Footer: React.FC = () => {
  const [status, setStatus] = React.useState("unknown");
  React.useEffect(() => {
    healthService.checkSystemHealth().then((data) => {
      setStatus(data?.status || "unknown");
    });
  }, []);

  return (
    <>
      <footer className="bg-sauna-navy py-16 text-white">
        <div className="container mx-auto px-4">
          {/* Main content area */}
          <div className="flex flex-col md:flex-row justify-between items-start gap-16 md:gap-32">
            {/* Left Section: Logo, Tagline, Status, Socials */}
            <div className="flex flex-col items-start flex-1 min-w-0 max-w-md">
              <div className="flex justify-start">
                <Logo size="sm" className="h-10 w-auto" />
              </div>
              <p className="text-sauna-beige/80 mt-6 mb-6">
                SaunaDip is Ireland & UK's first comprehensive directory for hot and cold wellness experiences, 
                connecting you with the best sauna and plunge locations.
              </p>
              {/* Service Status (Placeholder) */}
              <div className="flex items-center gap-4 text-sauna-beige/70 text-xs mt-4">
                <Link to="/system-status" className="hover:text-sauna-orange transition flex items-center gap-1">
                  {STATUS_ICON[status]}
                  <span>{STATUS_LABEL[status]}</span>
                </Link>
                <span className="mx-2">|</span>
                <Link to="/changelog" className="hover:text-sauna-orange transition">Changelog</Link>
              </div>
              {/* Social Icons (Placeholders) */}
              <div className="flex space-x-4 mt-8 mb-8">
                <Link to="/contact" className="text-sauna-beige/80 hover:text-sauna-orange transition">
                  <Mail size={20} />
                </Link>
                {/* Placeholder for Instagram Link - Replace # with your Instagram URL and update icon if needed */}
                <Link to={import.meta.env.VITE_INSTAGRAM_URL || "#"} className="text-sauna-beige/80 hover:text-sauna-orange transition">
                  <Instagram size={20} />
                </Link>
              </div>
            </div>

            {/* Right Section: Text Links in 3 Balanced Columns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-y-10 gap-x-24 flex-shrink-0 w-full md:w-auto min-w-[300px] md:min-w-[540px]">
              {/* Quick Links */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Quick Links</h3>
                <ul className="space-y-2">
                  <li>
                    <Link to="/" className="text-sauna-beige/80 hover:text-sauna-orange transition">
                      Home
                    </Link>
                  </li>
                  <li>
                    <Link to="/directory" className="text-sauna-beige/80 hover:text-sauna-orange transition">
                      Directory
                    </Link>
                  </li>
                  <li>
                    <Link to="/blog" className="text-sauna-beige/80 hover:text-sauna-orange transition">
                      Blog
                    </Link>
                  </li>
                  <li>
                    <Link to="/faq" className="text-sauna-beige/80 hover:text-sauna-orange transition">
                      FAQ
                    </Link>
                  </li>
                  <li>
                    <Link to="/about" className="text-sauna-beige/80 hover:text-sauna-orange transition">
                      About
                    </Link>
                  </li>
                   {/* Contact Us Link */}
                    <li>
                      <Link to="/contact" className="text-sauna-beige/80 hover:text-sauna-orange transition">
                        Contact Us
                      </Link>
                    </li>
                </ul>
              </div>

              {/* Legal Links */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Legal</h3>
                <ul className="space-y-2">
                  <li>
                    <Link to="/terms-and-conditions" className="text-sauna-beige/80 hover:text-sauna-orange transition">
                      Terms and Conditions
                    </Link>
                  </li>
                  <li>
                    <Link to="/privacy-policy" className="text-sauna-beige/80 hover:text-sauna-orange transition">
                      Privacy Policy
                    </Link>
                  </li>
                  <li>
                    <Link to="/cookie-policy" className="text-sauna-beige/80 hover:text-sauna-orange transition">
                      Cookie Policy
                    </Link>
                  </li>
                  <li>
                    <Link to="/acceptable-use-policy" className="text-sauna-beige/80 hover:text-sauna-orange transition">
                      Acceptable Use Policy
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Access Links */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Access</h3>
                <ul className="space-y-2">
                  <li>
                    <Link to="/choose-signup" className="text-sauna-beige/80 hover:text-sauna-orange transition">
                      Sign Up as Operator
                    </Link>
                  </li>
                  <li>
                    <Link to="/choose-signup" className="text-sauna-beige/80 hover:text-sauna-orange transition">
                      Sign Up as User
                    </Link>
                  </li>
                </ul>
                <div className="mt-3 text-xs text-sauna-beige/60">
                  Already have an account?{' '}
                  <Link to="/login" className="underline hover:text-sauna-orange transition">Log in</Link>
                </div>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-sauna-beige/20 mt-12 pt-8 text-center text-sm text-sauna-beige/60">
            <p>© {new Date().getFullYear()} SaunaDip • Built in Ireland 🇮🇪</p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
